import type { TFunction } from 'i18next';
import type { ComponentType } from 'react';

import { EOnboardingMilestone } from '@feathr/blackbox';
import { EOnboardingProgressiveFeatures } from '@feathr/extender/hooks';

import CreateAdCampaignModal from './CreateAdCampaignModal';

export enum EMilestoneActionType {
  Navigate = 'navigate',
  Modal = 'modal',
}

interface IMilestoneConfigBase {
  description: string;
  title: string;
}

interface IMilestoneConfigNavigate extends IMilestoneConfigBase {
  actionType?: EMilestoneActionType.Navigate;
  actionUrl: string;
  progressiveFeature?: EOnboardingProgressiveFeatures;
}

export interface IModalComponentProps {
  onClose: () => void;
}

interface IMilestoneConfigModal extends IMilestoneConfigBase {
  actionType: EMilestoneActionType.Modal;
  progressiveFeature: EOnboardingProgressiveFeatures;
  modalComponent: ComponentType<IModalComponentProps>;
  actionUrl?: string;
}

export type IMilestoneConfig = IMilestoneConfigNavigate | IMilestoneConfigModal;

export function getMilestoneConfig(
  milestone: EOnboardingMilestone,
  t: TFunction,
  localUrl: (path: string) => string,
): IMilestoneConfig {
  const milestoneMapping: Record<EOnboardingMilestone, IMilestoneConfig> = {
    [EOnboardingMilestone.InitialAccountSetup]: {
      actionUrl: localUrl('/quickstart/initial_account_setup'),
      description: t('This will guide you through configuring your Feathr account for use.'),
      title: t('Set up your Account'),
    },
    [EOnboardingMilestone.RetargetingCampaign]: {
      actionType: EMilestoneActionType.Modal,
      actionUrl: localUrl('/marketing/ads'),
      description: t(
        'This will guide you through the creation of a retargeting campaign in Feathr.',
      ),
      modalComponent: CreateAdCampaignModal,
      title: t('Publish your first Ad Campaign'),
      progressiveFeature: EOnboardingProgressiveFeatures.PublishFirstAdCampaign,
    },
    [EOnboardingMilestone.SingleSendCampaign]: {
      actionUrl: localUrl('/quickstart/single_send_campaign'),
      description: t(
        'This will guide you through creating an email marketing campaign, including template creation and list import.',
      ),
      title: t('Publish your first Email Campaign'),
      progressiveFeature: EOnboardingProgressiveFeatures.PublishFirstEmailCampaign,
    },
  };

  return milestoneMapping[milestone];
}

export function shouldMilestoneOpenModal(
  milestone: EOnboardingMilestone,
  t: TFunction,
  localUrl: (path: string) => string,
): boolean {
  const config = getMilestoneConfig(milestone, t, localUrl);
  return config.actionType === EMilestoneActionType.Modal;
}

export function getModalComponent(
  milestone: EOnboardingMilestone,
  t: TFunction,
  localUrl: (path: string) => string,
): ComponentType<IModalComponentProps> | null {
  const config = getMilestoneConfig(milestone, t, localUrl);
  if (config.actionType === EMilestoneActionType.Modal) {
    return config.modalComponent;
  }
  return null;
}
